import React, { FunctionComponent, Suspense, useEffect } from "react";
import styled from "styled-components";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";

import SEO from "./SEO";

import Footer from "components/Footer";
import ScrollToTop from "components/ScrollToTop/ScrollToTop";
import Header from "containers/Header";
import AppRoutes from "routes/routes-mapping";
import Modal from "containers/Modal";
import useOffersRefreshing from "hooks/useOffersRefreshing";
import useQueryParamSaver from "hooks/useQueryParamSaver";
import useTracking from "hooks/useTracking";
import { configState, fetchConfigFromAPI, setModal } from "state/config-slice";
import { AppDispatch } from "state/reducers-combiner";
import { userState, fetchUserFromAPI } from "state/user-slice";
import useAxiosInterceptor from "connection/useAxiosInterceptor";

import useDeeplink from "hooks/useDeeplink";
import useQueryCallbackHandler from "hooks/useQueryCallbackHandler";
import useCookieCart from "hooks/useCookieCart";
import getOpco from "utils/getOpco";
import { getPartnerImageUrl } from "utils/getImageUrl";
import { initDebug } from "utils/debugHelper";

const Loader = React.lazy(() => import(/* webpackChunkName: "Loader", webpackPrefetch: true */ "components/Loader"));
const IframeLayer = React.lazy(
  () => import(/* webpackChunkName: "IframeLayer", webpackPrefetch: true */ "containers/IframeLayer"),
);

const ContentWrapper = styled.div<{
  compensateFixedHeader: boolean;
}>`
  flex-grow: 1;
  position: relative;
  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    ${({ compensateFixedHeader }) =>
      compensateFixedHeader &&
      `
      margin-top: 63px;
    `}
  }

  ${({ compensateFixedHeader }) =>
    compensateFixedHeader &&
    `
    margin-top: 76px;
  `}
`;

export const LoaderPlaceholder = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  background-color: white;
  right: 0;
  left: 0;
  z-index: 1000;
`;

const App: FunctionComponent = () => {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector(userState);
  const config = useSelector(configState);
  const [cookies] = useCookies();
  const { cdnUrl } = useSelector(configState);
  const OpcolessCdnUrl = cdnUrl.replace(`/${getOpco()}/`, "/");
  const opco = getOpco();

  useAxiosInterceptor();
  useQueryParamSaver();
  useTracking();
  useOffersRefreshing(config.cdnUrl || "");

  useEffect(() => {
    dispatch(fetchConfigFromAPI());
  }, [dispatch]);

  useEffect(() => {
    if (config.loaded) {
      dispatch(fetchUserFromAPI());
    }
  }, [dispatch, config.loaded]);

  useDeeplink();

  useQueryCallbackHandler();

  useCookieCart();

  useEffect(() => {
    if (user.loggedIn && cookies.SUBSCRIPTIONPLUS_COOKIE_CONSENT_GRANTED === undefined) {
      dispatch(setModal({ showModal: true, cookieModal: true }));
    }
  }, [dispatch, cookies, user.loggedIn]);

  useEffect(() => {
    initDebug();

    // Favicon update logic
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    const faviconUrl =
      opco === "lw"
        ? `${getPartnerImageUrl("generic/lowi.ico", OpcolessCdnUrl)}`
        : `${getPartnerImageUrl("generic/favicon.ico", OpcolessCdnUrl)}`;

    if (link) {
      link.href = faviconUrl;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = faviconUrl;
      document.head.appendChild(newLink);
    }
  }, [opco, OpcolessCdnUrl]);

  return (
    <Suspense fallback={<LoaderPlaceholder />}>
      <SEO />
      <ScrollToTop />
      <Modal />
      <Loader />
      <IframeLayer />
      {user.loaded && (
        <>
          <Header />
          <ContentWrapper compensateFixedHeader={config.isMenuOpen}>
            <AppRoutes />
          </ContentWrapper>
          <Footer />
        </>
      )}
    </Suspense>
  );
};

export default App;
